import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MessagesSquare, Sparkles, X } from 'lucide-react';
import { loadBranded } from '../../utils/bootstrap';
import { isWhatsAppButtonEnabled } from '../../utils/siteFeatures';
import useHideOnScrollDown from '../../hooks/useHideOnScrollDown';
import './HelpLauncher.css';

// Site Widgets → Chat Buttons. "separate" (the default) keeps the two round
// buttons as they have always been; "combined" shows ONE "Help" button on
// phones that opens a small menu: the site's chat assistant or WhatsApp.
// Computers always keep both buttons — there is room for them there.
export const CHAT_BUTTONS_KEY = 'hm_chat_buttons';
export const readChatButtonsMode = () => {
    try {
        const v = JSON.parse(localStorage.getItem(CHAT_BUTTONS_KEY) || '{}');
        return v && v.phoneMode === 'combined' ? 'combined' : 'separate';
    } catch { return 'separate'; }
};

// The same test WhatsAppButton uses to decide whether it shows at all.
const whatsAppAvailable = () => {
    const c = loadBranded('hm_page_contact', 'contact', {}) || {};
    return isWhatsAppButtonEnabled() && !!String(c.whatsapp || c.phone || '').replace(/[^\d]/g, '');
};

// The chat assistant loads a moment after the page; a tap before that is
// remembered and the assistant opens itself as soon as it mounts.
const openChatbot = () => {
    if (window.__hmChatbotReady) window.dispatchEvent(new Event('hm-open-chatbot'));
    else window.__hmOpenChatbotPending = true;
};

function WAIcon() {
    return (
        <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
    );
}

// What counts as "a control the Help button must not cover". Links are left
// out on purpose: whole hotel cards are links, and the button would never show.
const CONTROL = 'button, input, select, textarea, label, [role="button"], [role="option"], '
    + '[role="checkbox"], [role="radio"], [role="tab"], [role="switch"], [role="slider"]';
const TEXT_FIELD = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"]), select, textarea, [contenteditable="true"]';

// True when a button or field sits under where the Help button rests.
// Measured from its resting spot (not its current one) so a tucked-away
// button can tell when it is safe to come back.
function coversControl(fab) {
    const cs = getComputedStyle(fab);
    const w = fab.offsetWidth;
    const h = fab.offsetHeight;
    const right = window.innerWidth - (parseFloat(cs.right) || 16);
    const bottom = window.innerHeight - (parseFloat(cs.bottom) || 24);
    const points = [
        [right - w / 2, bottom - h / 2],
        [right - w + 6, bottom - 6], [right - w + 6, bottom - h + 6],
        [right - 6, bottom - 6], [right - 6, bottom - h + 6],
    ];
    return points.some(([x, y]) => document.elementsFromPoint(x, y).some((el) => {
        if (el === fab || fab.contains(el)) return false;
        if (el.closest('.hm-help-menu, .hm-help-backdrop, .mbn-root')) return false;
        return !!el.closest(CONTROL);
    }));
}

/** @param {boolean} chatbotOn  the site's chat assistant is shown on this page */
export default function HelpLauncher({ chatbotOn }) {
    const { t } = useTranslation();
    const location = useLocation();
    const [mode, setMode] = useState(readChatButtonsMode);
    const [waOn, setWaOn] = useState(whatsAppAvailable);
    const [open, setOpen] = useState(false);
    const tuckAway = useHideOnScrollDown();
    const fabRef = useRef(null);
    // Steps aside while it would cover a control (an open dropdown, the
    // cabin-class buttons, a form's last field) or while the visitor types.
    const [blocking, setBlocking] = useState(false);
    const [typing, setTyping] = useState(false);

    useEffect(() => {
        const read = () => { setMode(readChatButtonsMode()); setWaOn(whatsAppAvailable()); };
        window.addEventListener('hm-settings-loaded', read);
        return () => window.removeEventListener('hm-settings-loaded', read);
    }, []);
    useEffect(() => { setOpen(false); }, [location.pathname]);

    // Only worth combining when there are two things to combine.
    const active = mode === 'combined' && chatbotOn && waOn;
    useEffect(() => {
        document.documentElement.toggleAttribute('data-chat-combined', active);
        return () => document.documentElement.removeAttribute('data-chat-combined');
    }, [active]);
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open]);

    useEffect(() => {
        if (!active) return undefined;
        let timer = 0;
        const check = () => {
            clearTimeout(timer);
            // Let a dropdown that the tap opened render first.
            timer = setTimeout(() => {
                if (fabRef.current) setBlocking(coversControl(fabRef.current));
            }, 140);
        };
        const onFocusIn = (e) => setTyping(!!(e.target && e.target.matches && e.target.matches(TEXT_FIELD)));
        const onFocusOut = () => setTyping(false);
        document.addEventListener('click', check, true);
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        document.addEventListener('focusin', onFocusIn);
        document.addEventListener('focusout', onFocusOut);
        check();
        return () => {
            clearTimeout(timer);
            document.removeEventListener('click', check, true);
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            document.removeEventListener('focusin', onFocusIn);
            document.removeEventListener('focusout', onFocusOut);
        };
    }, [active, location.pathname]);

    if (!active) return null;

    const hidden = !open && (tuckAway || blocking || typing);

    const pick = (fn) => () => { setOpen(false); fn(); };
    return (
        <>
            {open && <div className="hm-help-backdrop" onClick={() => setOpen(false)} aria-hidden="true" />}
            {open && (
                <div className="hm-help-menu" role="menu" aria-label={t('help.menuLabel', 'How can we help?')}>
                    <p className="hm-help-menu__title">{t('help.menuLabel', 'How can we help?')}</p>
                    <button type="button" role="menuitem" className="hm-help-item" onClick={pick(openChatbot)}>
                        <span className="hm-help-item__icon hm-help-item__icon--chat"><Sparkles size={19} /></span>
                        <span className="hm-help-item__text">
                            <strong>{t('help.chatTitle', 'Chat with us')}</strong>
                            <small>{t('help.chatNote', 'Instant answers about hotels, trips and bookings')}</small>
                        </span>
                    </button>
                    <button type="button" role="menuitem" className="hm-help-item" onClick={pick(() => window.dispatchEvent(new Event('hm-open-whatsapp')))}>
                        <span className="hm-help-item__icon hm-help-item__icon--wa"><WAIcon /></span>
                        <span className="hm-help-item__text">
                            <strong>WhatsApp</strong>
                            <small>{t('help.waNote', 'Message our team')}</small>
                        </span>
                    </button>
                </div>
            )}
            <button type="button" ref={fabRef} className={`hm-help-fab${open ? ' is-open' : ''}`}
                aria-haspopup="menu" aria-expanded={open}
                onClick={() => setOpen((o) => !o)}
                tabIndex={hidden ? -1 : undefined}
                style={{ translate: hidden ? '160% 0' : '0 0' }}>
                {open ? <X size={20} aria-hidden="true" /> : <MessagesSquare size={20} aria-hidden="true" />}
                <span>{open ? t('common.close', 'Close') : t('help.button', 'Help')}</span>
            </button>
        </>
    );
}
